import { Stack, Typography } from 'common/design-system/components-v2';
import SimpleGenericFilterPanel from './SimpleFilterPanel/SimpleGenericFilterPanel';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GridApi, GridReadyEvent } from 'ag-grid-community';
import { IFiltersValues } from 'common/components/FilterPanel/FilterPanel.interface';
import SimpleGenericTable from './SimpleTable/SimpleGenericTable';
import { IDataItem, IGenericPageProps } from './interfaces';
import { useTranslation } from 'react-i18next';
import { calcFilteredData } from './SimpleFilterPanel/simpleFilterPanelFunctions';

export const SimpleGenericPage = <T,>({
    actions,
    translationKey,
    pagination,
    filterId,
    items,
    savedFiltersComponentName,
    filterIds,
    tableRegistry,
    localFilters,
    onItemClicked,
    isDataLoading,
    exportButtons,
    setFilteredDataForExport,
}: IGenericPageProps<T>) => {
    const [rawData, setRawData] = useState([] as IDataItem[]);
    const [filteredData, setFilteredData] = useState<IDataItem[]>([]);
    const [isLoading, setIsLoading] = useState(isDataLoading);
    const apiRef = useRef<GridApi>();
    const { t } = useTranslation(translationKey);

    useEffect(() => {
        setIsLoading(isDataLoading);
    }, [isDataLoading]);

    const onGridReady = useCallback((params: GridReadyEvent) => {
        apiRef.current = params.api;
    }, []);

    const init = useCallback(async () => {
        setRawData(items);
        setIsLoading(false);
    }, [items]);

    useEffect(() => {
        void init();
    }, [init]);

    const isFilterEmpty = (filtersValues: IFiltersValues) => {
        return Object.keys(filtersValues).find((filterKey) => filtersValues[filterKey] !== '') === undefined;
    };

    const onFilterChangeCallBack = React.useCallback(
        (filtersValues: IFiltersValues) => {
            if (isFilterEmpty(filtersValues)) {
                setFilteredData(rawData);
                setFilteredDataForExport && setFilteredDataForExport(rawData);
                return;
            }
            const newFilteredData = calcFilteredData(localFilters, filtersValues, rawData);
            if (JSON.stringify(filteredData) !== JSON.stringify(newFilteredData)) {
                setFilteredData(newFilteredData);
                setFilteredDataForExport && setFilteredDataForExport(newFilteredData);
            }
        },
        [localFilters, rawData, filteredData, setFilteredDataForExport],
    );

    return (
        <Stack fullHeight fullWidth padding={4}>
            <Stack fullHeight>
                <Stack spacing={5} style={{ height: '50px' }}>
                    <SimpleGenericFilterPanel
                        filterId={filterId}
                        savedFiltersComponentName={savedFiltersComponentName}
                        translationKey={translationKey}
                        tableRegistry={tableRegistry}
                        filterIds={filterIds}
                        localFilters={localFilters}
                        rawData={rawData}
                        tableData={filteredData}
                        isLoading={!!isLoading}
                        onFilterChange={onFilterChangeCallBack}
                    />
                </Stack>
                <Stack style={{ flexGrow: 1 }}>
                    {isLoading ? (
                        <Stack justifyContent={'center'} alignItems={'center'} fullHeight={true}>
                            <Typography variant={'bodyLg'}>{t('GENERAL.LOADING')}</Typography>
                        </Stack>
                    ) : (
                        <SimpleGenericTable
                            pagination={pagination}
                            rowSelectionMode='single'
                            actions={actions}
                            tableRegistry={tableRegistry}
                            translationKey={translationKey}
                            onInnerGridReady={onGridReady}
                            data={filteredData}
                            allItemsCount={rawData.length}
                            pageSize={20}
                            onItemClicked={onItemClicked}
                            exportButtons={exportButtons}
                        />
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
};
